// ─── Angular Imports ───────────────────────────────────────────────────────────
import { Component, OnInit, AfterViewInit, HostListener, ChangeDetectionStrategy } from '@angular/core';

// ─── Third-Party Imports ───────────────────────────────────────────────────────
import Chart from 'chart.js/auto';

// ─── Application Imports ───────────────────────────────────────────────────────
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { BatchesService } from 'src/app/services/batches.services';
import { BatchListResult } from 'src/app/models/batches/batch-result';
import { MenuService } from 'src/app/services/menu.service';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class BarChartComponent implements OnInit, AfterViewInit {
  // ─── State ───────────────────────────────────────────────────────────────────
  Permission = Permission;
  public chart: any;
  firstLoad: boolean = true;
  chartRef!: Chart<'bar', any, string>;
  aspectRatio: number = 5;

  // ─── Constructor ─────────────────────────────────────────────────────────────
  constructor(
    private menuService: MenuService,
    private batchesService: BatchesService,
    public authService: AuthService,
  ) {}

  // ─── Lifecycle Hooks ────────────────────────────────────────────────────────
  ngOnInit(): void {
    if (!this.authService.hasPermission(Permission.BatchesRead)) {
      return;
    }
    this.createChart();
  }

  ngAfterViewInit(): void {
    this.menuService.currentMenuStatus.subscribe((status) => {
      if (this.firstLoad) {
        this.firstLoad = false;
        return;
      }
      const chartCtn = document.getElementById('chartCtn');
      if (chartCtn) {
        chartCtn.style.height = chartCtn.clientWidth / this.aspectRatio + 'px';
      }
    });
  }

  // ─── Chart Methods ──────────────────────────────────────────────────────────
  createChart(): void {
    const data: any = {
      pending: [],
      active: [],
      rejected: [],
      complete: [],
    };
    const labels: string[] = [];
    const status: ('pending' | 'active' | 'rejected' | 'complete')[] = [
      'pending',
      'active',
      'rejected',
      'complete',
    ];

    this.batchesService
      .listBatches(this.authService.hasPermission(Permission.BatchesFull), 5)
      .subscribe((result: BatchListResult) => {
        const batches = result?.batches || [];
        if (batches.length === 0) {
          return;
        }

        let barThickness = this.calculateBarThickness(window.innerWidth);
        this.aspectRatio = this.calculateAspectRatio(batches.length);

        batches.forEach((batch) => {
          const total = batch.productCount;
          data['complete'].push((batch.productsReleasedCount * 100) / total);
          data['pending'].push(((total - batch.productsReleasedCount) * 100) / total);
          labels.push(batch.name);
        });

        this.chartRef = new Chart('myBarChart', {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [
              {
                label: 'Completed',
                data: data.complete,
                backgroundColor: '#1AA367',
                barThickness: barThickness,
              },
              {
                label: 'Active',
                data: data.active,
                backgroundColor: '#FFBE21',
                barThickness: barThickness,
              },
              {
                label: 'Rejected',
                data: data.rejected,
                backgroundColor: '#DD2C00',
                barThickness: barThickness,
              },
              {
                label: 'Pending',
                data: data.pending,
                backgroundColor: '#858C94',
                barThickness: barThickness,
              },
            ],
          },
          options: {
            aspectRatio: this.aspectRatio,
            indexAxis: 'y',
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                yAlign: 'bottom',
                xAlign: 'left',
                bodyAlign: 'center',
                callbacks: {
                  label: (context) => {
                    switch (context.dataset.label) {
                      case 'Completed':
                        return `Complete: ${batches[context.dataIndex].productsReleasedCount}`;
                      case 'Active':
                        return `Active: ${batches[context.dataIndex].clientReview?.active}`;
                      case 'Rejected':
                        return `Rejected: ${batches[context.dataIndex].clientReview?.rejected}`;
                      default:
                        return `Pending: ${
                          batches[context.dataIndex].productCount -
                          batches[context.dataIndex].productsReleasedCount
                        }`;
                    }
                  },
                },
              },
            },
            scales: {
              x: {
                stacked: true,
                ticks: {
                  callback: (value) => `${value}%`,
                },
                max: 100,
              },
              y: {
                stacked: true,
                ticks: {
                  callback: (value, index) => {
                    const batch = batches[index];
                    const label = `${batch?.jobNumber}: ${batch?.name}`;
                    return label.length < 20 ? label : `${label.substring(0, 17)}...`;
                  },
                },
              },
            },
          },
        });
      });
  }

  // ─── Utility Methods ────────────────────────────────────────────────────────
  calculateBarThickness(width: number): number {
    switch (true) {
      case width > 1400:
        return 25;
      case width > 1200:
        return 20;
      case width > 900:
        return 15;
      case width > 600:
        return 10;
      default:
        return 5;
    }
  }

  calculateAspectRatio(batchCount: number): number {
    switch (batchCount) {
      case 5:
        return 6;
      case 4:
        return 8;
      case 3:
        return 10;
      case 2:
        return 12;
      default:
        return 15;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    const chartCtn = document.getElementById('chartCtn');
    if (chartCtn && this.chartRef) {
      chartCtn.style.height = chartCtn.clientWidth / this.aspectRatio + 'px';
      const barThickness = this.calculateBarThickness(window.innerWidth);
      this.chartRef.data.datasets.forEach((dataset) => {
        dataset.barThickness = barThickness;
      });
    }
  }
}
